import { MutationTree } from "vuex";
import { PharmaciesMutations, PharmaciesState } from "@/store/pharmacies/types";
import {
  addDays,
  addHours,
  addMonths,
  differenceInCalendarMonths,
  format,
  isFirstDayOfMonth,
  parse,
  startOfMonth,
} from "date-fns";
import {
    AdditionalData, AbsenceAdditional,
    DateKeys,
    HumanFRV,
    NewUser, VacationItem,
    Shift,
    ShiftDetails,
    ShiftInfoItem,
    TimeRange,
} from '@/types';
import { ru } from "date-fns/locale";
import store from "@/store";
import { extractDatePartsGURV } from "@/utils/gurv";
import { frvTypeList } from '@/utils/main';
import { VacationFlag, vacationTypes } from '@/utils/vacation';

// Создание объекта с ФРВ по умолчанию (OMR-3075)
const emptyFRVObj = (isExternalUser: boolean, zupGuid: string, pharmId: number | null) => {
    return {
        date: '',
        zupGuid: zupGuid ?? '',
        numberVs: 0,
        pharmId: pharmId ?? 0,
        frvNorm: 0,
        frvFact: 0,
        frvFactRv: 0,
        frvType: isExternalUser ? 'external' : null, // Разрешаем считать "нулевой" ФРВ для внешних пользователй
        frvLimit: false,
        frvSchedule: 0,
        frvNew: 0,
        frvRvNew: 0,
        frvRvSchedule: 0
    }
}

const mutations: MutationTree<PharmaciesState> & PharmaciesMutations = {
    setPharmaciesId(state, payload) {
        state.pharmacies_arr = payload
        state.pharmId = state.pharmacies_arr[0].id
        if (localStorage.getItem('pharmId') && state.pharmacies_arr[0].id !== Number(localStorage.getItem('pharmId'))) {
            state.pharmId = Number(localStorage.getItem('pharmId'))
        }
    },
    setTomorrowWorkTime(state) {
        state.tomorrow_worktime = state.pharmacies_arr.find(pharm => pharm.id === state.pharmId)?.tomorrowWorkTime ?? null
    },
    clearTomorrowWorkTime(state) {
        state.tomorrow_worktime = null
    },
    setPharmId(state, payload) {
        state.pharmId = payload
    },
    setPharmList(state, payload) {
        state.pharms_list = payload
    },
    setPharmCount(state, payload) {
        state.pharms_count = payload.meta.count
    },
    setPharmSkip(state, payload) {
        state.pharms_skip = payload
    },
    setPharmSearchQuery(state, payload) {
        state.pharms_search = payload
        if (payload == null) {
            localStorage.removeItem('pharmSearch')
        }
    },
    increasePharmQuery(state) {
        if (state.pharms_skip < state.pharms_count) {
            state.pharms_skip += 10
        }
    },
    decreasePharmQuery(state) {
        if (state.pharms_skip > 0) {
            state.pharms_skip -= 10
        }
    },
    setPharmQueryValue(state, payload) {
        state.pharms_skip = payload
    },
    deleteUserShift(state, payload) {
        state.pharm_users.forEach((item) => {
            if (item.timeShift && payload.zupGuid === item.zupGuid) {
                item.timeShift[payload.dateFrom].forEach((shift, index) => {
                    if (item.timeShift && shift.id === payload.id) {   
                        item.timeShift[payload.dateFrom].splice(index, 1)
                    }
                })
            }
        })
    },
    setUsers(state, payload) {
        state.pharm_users = []
        state.pharmacies_arr.forEach((item) => {
            if (item.id === payload) {
                item.users.forEach(userItem => {
                    const userName = `${userItem.lastName} ${userItem.firstName} ${userItem.middleName}`
                    userItem.serviceNumbers.forEach(user => {
                        state.pharm_users?.push({
                            name: userName,
                            position: user.position,
                            positionCode: user.positionCode,
                            bid: user.bid,
                            zupGuid: user.zupGuid,
                            frv: emptyFRVObj(false, user.zupGuid, state.pharmId),
                            id: userItem.id,
                            timeShift: {},
                            scheduleTimeShift: {},
                            dateArr: null,
                            timeZone: item.pharmTz,
                            grade: user.grade,
                            hasVacation: false,
                            numberVs: 0,
                            externalUser: false,
                            profiPstUser: false,
                            newUser: false,
                            phone: userItem.phone || null,
                            dateOfEmployment: user.dateOfEmployment || null,
                            dateDismissal: user.dateDismissal || null,
                        })
                    })
                })
            }
        })
    },
    setExternalUsers(state, payload) {
        const pharmTz = state.pharmacies_arr[0].pharmTz

        // Создание объекта пользователя
        const createUser = (
          name: string,
          position: string,
          positionCode: string,
          bid: number,
          zupGuid: string,
          id: string,
          timeZone: number,
          grade: number,
          externalUser: boolean,
          phone: string | null,
          dateDismissal: string | null
        ) => {
          return {
            name,
            position,
            positionCode,
            bid,
            zupGuid,
            frv: emptyFRVObj(true, zupGuid, state.pharmId),
            id,
            timeShift: {},
            scheduleTimeShift: {},
            dateArr: null,
            timeZone,
            grade,
            hasVacation: false,
            numberVs: 0,
            externalUser,
            profiPstUser: false,
            newUser: false,
            phone,
            dateDismissal
          };
        };

        // Очистка внешних пользователей
        const clearExternalUsers = () => {
            const hasExternalUsers = state.pharm_users.filter(el => el.externalUser)

            if (hasExternalUsers) {
                state.pharm_users = state.pharm_users.filter(el => !el.externalUser)
            }
        }

        if (!payload) {
           return clearExternalUsers() // Если не пришёл payload, сразу убираем внешних пользователей
        }

        // Получаем только смены с пользователями из других аптек
        const filteredShifts = payload.filter((shift: Shift) => {
            const users = Array.isArray(shift.user) ? shift.user : [shift.user]; //  Преобразуем в массив, если объект (из-за типизации User[])

            return users.some(user =>
              user.serviceNumbers?.length && user.serviceNumbers.some(el => el.pharmId !== state.pharmId)
            );
        });

        if (filteredShifts.length === 0) {
            return clearExternalUsers() // Если нет подходящих смен, убираем внешних пользователей
        }

        const exchangeUsers: NewUser[] = []; // Пользователи по Беру
        const mainUsers: NewUser[] = []; // Внешние пользователи

        filteredShifts.forEach(shift => {
            const users = Array.isArray(shift.user) ? shift.user : [shift.user]; // Преобразуем в массив, если объект (из-за типизации User[])

            for (const user of users) {
                if (!user.serviceNumbers || user.serviceNumbers.length === 0) continue; // Пропускаем, если serviceNumbers пустой

                for (const service of user.serviceNumbers) {
                    if (service.pharmId === state.pharmId) continue; // Пропускаем, если pharmId совпадает
                    
                    const newUser = createUser(
                      `${user.lastName} ${user.firstName} ${user.middleName}`,
                      service.position,
                      service.positionCode,
                      service.bid,
                      service.zupGuid,
                      user.id,
                      pharmTz,
                      service.grade,
                      true,
                      user.phone || null,
                      service.dismissalUser?.dismissalAt || null
                    );

                    // Отмечаем ПрофиПСТ, как и приходящих сотрудников из setProfiPstUsers
                    if (service.profiPst) {
                        newUser.profiPstUser = true
                        newUser.externalUser = true
                    }

                    // Разделение пользователей по типу смены
                    (shift.shiftType === "Main" ? mainUsers : exchangeUsers).push(newUser);
                }
            }
        });

        // Добавляем пользователей с текущей аптеки (отфильтровав внешних, которые были добавлены ранее)
        const existingUsers: NewUser[] = state.pharm_users.filter(user => !user.externalUser);

        // Обновляем список пользователей в необходимом порядке
        state.pharm_users = [...existingUsers, ...exchangeUsers, ...mainUsers];

        // Убираем дубликаты в состоянии с пользователями
        state.pharm_users = state.pharm_users?.filter((user, index, self) =>
          index === self.findIndex(u => u.zupGuid === user.zupGuid)
        )

        // Формируем Set из zupGuid для выявления неактуальных внешних пользователей
        const allowedUsers = new Set([...exchangeUsers, ...mainUsers].map(u => u.zupGuid));

        // Оставляем только пользователей аптеки, внешних и по Беру
        state.pharm_users = state.pharm_users.filter(user =>
          !user.externalUser || allowedUsers.has(user.zupGuid)
        );
    },
    setProfiPstUsers(state, payload) {
        state.pharm_pst_users = []
        state.pharmacies_arr.forEach((item) => {
            if (item.id === payload) {
                item.usersProfi?.forEach(userItem => {
                    const userName = `${userItem.lastName} ${userItem.firstName} ${userItem.middleName}`
                    userItem.serviceNumbers.forEach(user => {
                        state.pharm_users.push({
                            name: userName,
                            position: user.position,
                            positionCode: user.positionCode,
                            bid: user.bid,
                            zupGuid: user.zupGuid,
                            frv: emptyFRVObj(false, user.zupGuid, state.pharmId),
                            id: userItem.id,
                            timeShift: {},
                            scheduleTimeShift: {},
                            dateArr: null,
                            timeZone: item.pharmTz,
                            grade: user.grade,
                            hasVacation: false,
                            numberVs: 0,
                            externalUser: false,
                            profiPstUser: true,
                            newUser: false,
                            phone: userItem.phone || null,
                            dateOfEmployment: user.dateOfEmployment || null,
                            dateDismissal: user.dateDismissal || null,
                        })
                    })
                })
            }
        })
    },
    setFondWorkTime(state, payload) {
        state.FRV = payload
        state.corestaff_count_panel = {
            dedicated: payload.frvMainPlan,
            actual: payload.frvMainFact,
            planned: payload.frvMainSchedule,
            new: 0
        }
        state.frv_rv_count_panel = {
            actual: payload.frvRvMainFact,
            planned: payload.frvRvMainSchedule,
            new: 0
        }
    },
    // Сброс ФРВ по аптеке в шапке при ошибке получения с сервера
    resetFondWorkTime(state) {
        state.corestaff_count_panel = { dedicated: 0, actual: 0, planned: 0, new: 0 }
        state.frv_rv_count_panel = { actual: 0, planned: 0, new: 0 }
    },
    // ФРВ для модалки создания потребности Беру
    setBeruFondWorkTime(state, payload) {
        state.beru_corestaff_count_panel = {
            dedicated: payload.frvMainPlan,
            actual: payload.frvMainFact,
            planned: payload.frvMainSchedule,
            new: 0
        }
        state.beru_frv_rv_count_panel = {
            actual: payload.frvRvMainFact,
            planned: payload.frvRvMainSchedule,
            new: 0
        }
    },
    saveMainFRV(state) {
        state.old_corestaff = state.corestaff_count_panel ? state.corestaff_count_panel.planned : 0
        state.old_frv_rv = state.frv_rv_count_panel ? state.frv_rv_count_panel.planned : 0
    },
    changeMainFRV(state, payload) {
        if (state.corestaff_count_panel && state.corestaff_count_panel.planned > 0) {
            state.corestaff_count_panel ? state.corestaff_count_panel.planned = (state.corestaff_count_panel.planned + payload.frv * -1) : null
        }
        if (state.frv_rv_count_panel && state.frv_rv_count_panel.planned > 0) {
            state.frv_rv_count_panel ? state.frv_rv_count_panel.planned = (state.frv_rv_count_panel.planned + payload.frvRV * -1) : null
        }
    },
    resetMainFRV(state) {
        state.corestaff_count_panel ? state.corestaff_count_panel.planned = state.old_corestaff : null
        state.frv_rv_count_panel ? state.frv_rv_count_panel.planned = state.old_frv_rv : null
    },
    setTimeSchedule(state, payload) {
        let newTimeSchedule = {}

        /* Проверяем на наличие и соответствие количества дней в месяце и пришедших с сервера,
           создаём пустые дни если даты не пришли */
        if (Object.entries(payload).length === 0 || (Object.entries(payload).length !== store.getters['getCurrentDays'].length)) {
            const firstDateOfMonth = store.getters['getSelectedDate'].split('.')
            const schedulePayload = payload;

            // Получаем текущую дату для заполнения пропущенных дней
            const daysInMonth = store.getters['getCurrentDays'].length;
            const month = firstDateOfMonth[1];
            const year = firstDateOfMonth[2];

            for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${day}.${month}.${year}`;
                if (!schedulePayload[dateStr]) {
                    schedulePayload[dateStr] = {
                        actualWorkTime: '00:00-00:00',
                        isDifference: true,
                        emptySchedule: true
                    };
                }
            }

            // Сортируем обновленный объект с датами
            const updatedSchedulePayload = Object.entries(schedulePayload).sort((a, b) => {
                const dayA = parseInt(a[0].split('.')[0], 10);
                const dayB = parseInt(b[0].split('.')[0], 10);
                return dayA - dayB;
            });

            newTimeSchedule = Object.fromEntries(updatedSchedulePayload)
        } else {
            // Пропущенных дней нет, заполняем всё с сервера
            newTimeSchedule = payload
        }

        // Добавляем новые данные (isDayFull) в расписание
        if (state.time_schedule_details) {
            const newObj = { ...newTimeSchedule }

            // Объединяем свойства с основным объектом графика
            for (const key in state.time_schedule_details) {
                if (newObj[key]) {
                    newObj[key] = { ...newObj[key], ...state.time_schedule_details[key] }
                } else {
                    newObj[key] = state.time_schedule_details[key]
                }
            }

            newTimeSchedule = newObj
        }

        state.time_schedule = newTimeSchedule

        const isDayPassAndFull = (key) => {
            const [day, month, year] = key.split(".").map(Number);
            const date = new Date(year, month - 1, day);
            if (store.getters.getGurvRole) {
                return newTimeSchedule[key].dayFull || ''
            }
            if (addDays(new Date(), -3).getTime() < date.getTime()) {
                return newTimeSchedule[key].dayFull || ''
            }
        }

        // Проверка на открытую модалку создания смены для предотвращения закрытия (OMR-3256)
        const isOpenedCreateShift = (item: NewUser, index: number) => {

            if (state.active_shift_details && state.active_shift_details.zupGuid === item.zupGuid) {
                if (state.active_shift_details.payload[0] == index) {
                    return true;
                }
            }
            return false
        }

        state.pharm_users.map((item:NewUser) => {
            item.dateArr = state.time_schedule_key = Object.keys(newTimeSchedule).map((key: string, index: number) => {
                const [day, month, year] = key.split(".").map(Number);
                const date = new Date(year, month - 1, day);
                const currentDay = date.getDay();
                const currentDate = date.getDate();
                const today = new Date();
                const res: DateKeys = {
                    key,
                    openCreateShift: isOpenedCreateShift(item, index),
                    dayOut: currentDay === 0 || currentDay === 6,
                    holiday: state.pharm_holidays?.includes(key),
                    today: today.getDate() === currentDate && today.getMonth() === date.getMonth(),
                    actualWorkTime: newTimeSchedule[key].actualWorkTime,
                    isDifference: newTimeSchedule[key].isDifference,
                    dayFull: isDayPassAndFull(key),
                };

                return res;
            })
            return item
        })
    },
    setTimeScheduleDetails(state, payload) {
        state.time_schedule_details = {}
        state.time_schedule_details = payload
    },
    setPastTimeScheduleDetails(state, payload) {
        state.time_schedule_details_past = {}
        state.time_schedule_details_past = payload
    },
    removeOldShift(state) {
        state.oldShift = null
    },
    openCreateShift(state, payload) {
        state.oldShift = payload

        // Сохраняем открытое окно создание смены (OMR-3256)
        const openedItem: ShiftDetails = {
            zupGuid: state.pharm_users[state.oldShift[1]].zupGuid,
            payload
        }

        if (payload) {
            state.active_shift_details = openedItem
        }

        if (state.oldShift) {
            const dateArr = state.pharm_users[state.oldShift[1]].dateArr
            if (dateArr) {
                dateArr[state.oldShift[0]].openCreateShift = true
            }
        }

    },
    closeCreateShift(state) {
        if (state.oldShift && state.pharm_users[state.oldShift[1]]) {
            const dateArr = state.pharm_users[state.oldShift[1]].dateArr
            if (dateArr && dateArr[state.oldShift[0]].openCreateShift) {
                dateArr[state.oldShift[0]].openCreateShift = false
                state.active_shift_details = null // Очищаем сохранение открытого окна создания смены (OMR-3256)
            }
        }
    },
    setAdminShift(state, payload) {
        state.pharm_users.forEach((user) => {
            user.timeShift = {}
        });

        // Добавление пришедших смен пользователям
        payload.forEach((item) => {
            const timeZone = item.shiftTz + (new Date().getTimezoneOffset() / 60)
            const shiftDateFrom = format(addHours(new Date(item.dateFrom), timeZone), 'd.M.yyyy')
            const shiftDateTo = format(addHours(new Date(item.dateTo), timeZone), 'd.M.yyyy')
            const dateTo = format(addHours(new Date(item.dateTo), timeZone), 'HH:mm')
            const dateFrom = format(addHours(new Date(item.dateFrom), timeZone), 'HH:mm')
            const timeDiff = Math.floor((+new Date(item.dateTo) - +new Date(item.dateFrom)) / 1000 / 3600)
            const firstDayOfMonth = isFirstDayOfMonth(addHours(new Date(item.dateTo), timeZone))
            const isNewMoth = format(new Date(item.dateTo), 'MMMM', {locale: ru}) != format(new Date(item.dateFrom), 'MMMM', {locale: ru})
            const big = dateTo <= dateFrom
            let diner:string | null = null

            if (timeDiff < 9 && timeDiff >= 5) {
                diner = '30 минут'
            } else if (timeDiff < 13 && timeDiff >= 9) {
                diner = '1 час'
            } else if (timeDiff >= 13) {
                diner = '1 час 30 минут'
            } else {
                diner = null
            }
            state.pharm_users.forEach((user) => {
                if (user.zupGuid === item.zupGuid) {
                    if (user.timeShift) {
                        // Убираем у новых внешних пользователей флаг удаления
                        if (user.externalUser && user.newUser) {
                            user.newUser = false
                        }

                        if (!user.timeShift[shiftDateFrom]) {
                            user.timeShift[shiftDateFrom] = []
                        }

                        const isPassed = () => {
                            // Проверка, что ГУРВ может создавать смены в определённом диапазоне в прошлом
                            const { creatingDay, creatingMonth, currentMonthNumber, currentDay } = extractDatePartsGURV(shiftDateFrom);
                            if (store.getters.getGurvRole) {
                                // Переходящие смены созданные в позапрошлом месяце
                                const previousMonthDate = startOfMonth(addMonths(new Date(), -1)) // прошлый месяц
                                const shiftStartDate = parse(shiftDateFrom, "d.M.yyyy", new Date()) // дата начала смены
                                return differenceInCalendarMonths(shiftStartDate, previousMonthDate) < 0; // считаем прошедшими все переходящие с позапрошлого месяца
                            }

                            // С 17 числа по дате текущего месяца запрещаем менять смены 15 числа
                            if (currentDay >= 17 && (creatingDay === 15 && creatingMonth === currentMonthNumber)) {
                                return true
                            }

                            // 2 числа по дате текущего месяца запрещаем менять смены на последние числа прошлого месяца (для всех ролей)
                            if (currentDay === 2 && (creatingDay <= 31 && creatingMonth !== currentMonthNumber)) {
                                return true
                            }

                            return addDays(new Date(), -3).getTime() > parse(shiftDateFrom, "d.M.yyyy", new Date()).getTime()
                        }

                        const userShift = {
                            zupGuid: item.zupGuid,
                            dateFrom: shiftDateFrom,
                            dateTo: shiftDateTo,
                            timeTo: dateTo,
                            timeFrom: dateFrom,
                            big: isNewMoth ? false : big,
                            status: item.shiftStatus,
                            type: item.shiftType,
                            diner: diner,
                            id: item.id,
                            timeZone: timeZone,
                            cancelationReason: item.cancelationReason,
                            cancelationComment: item.cancelationComment,
                            passed: isPassed()
                        }

                        // Проверяем, есть ли в массиве объект с таким же id
                        const isDuplicate = user.timeShift[shiftDateFrom].some(item => {
                            return item.id === userShift.id;
                        });

                        if (!isDuplicate) {
                            user.timeShift[shiftDateFrom] = [userShift, ...user.timeShift[shiftDateFrom]];
                        }

                        if (isNewMoth && firstDayOfMonth) {
                            if (!user.timeShift[shiftDateTo]) {
                                user.timeShift[shiftDateTo] = []
                            }

                            const isDuplicate = user.timeShift[shiftDateTo].some(item => {
                                return item.id === userShift.id;
                            });

                            if (!isDuplicate) {
                                user.timeShift[shiftDateTo] = [userShift, ...user.timeShift[shiftDateTo]]
                            }
                        }
                        user.timeShift[shiftDateFrom].sort((a: TimeRange, b: TimeRange) => {
                            // Преобразуем строки в объекты Date для корректной сортировки
                            const dateA = new Date(`2020-01-01T${a.timeFrom}`);
                            const dateB = new Date(`2020-01-01T${b.timeFrom}`);
                            return dateA.getTime() - dateB.getTime();
                        });
                    }
                }
            })
        })

        // Добавление внешним пользователям флага удаления если нет смен и смены не пришли с api/admin-shift
        state.pharm_users.forEach((user: NewUser) => {
            if (user.externalUser && (user.timeShift && Object.values(user.timeShift).length === 0)) {
                user.newUser = true;
            }
        })

        // Добавление кадровых мероприятий
        state.pharm_users.map((user) => {
            const timeShift: object | undefined = user.timeShift;
            const scheduleTimeShift: object | undefined = user.scheduleTimeShift;

            const newTimeShift: AdditionalData = {};

            // Копируем массив значений из timeShift и добавляем свойства из scheduleTimeShift в начало нового массива
            for (const key in timeShift) {
                if (Object.prototype.hasOwnProperty.call(timeShift, key)) {
                    newTimeShift[key] = [...timeShift[key]];

                    if (!scheduleTimeShift) return

                    // Проверяем что obj2[key] есть и не равен undefined
                    if (scheduleTimeShift[key] && JSON.stringify(newTimeShift[key][0]) !== JSON.stringify(scheduleTimeShift[key])) {
                        newTimeShift[key].unshift(scheduleTimeShift[key]);
                    }
                }
            }

            // Добавляем все кадровые мероприятия из scheduleTimeShift в новый массив (КМ должны быть в своём массиве)
            for (const key in scheduleTimeShift) {
                if (Object.prototype.hasOwnProperty.call(scheduleTimeShift, key) && !Object.prototype.hasOwnProperty.call(timeShift, key)) {
                    newTimeShift[key] = [scheduleTimeShift[key]];
                }
            }
            user.timeShift = newTimeShift;
        })
    },
    setPharmSchedule(state, payload) {
        state.pharm_users.forEach(user => {
            user.scheduleTimeShift = {}
            user.hasVacation = false

            const userData = payload[user.zupGuid]
            if (!userData) return

            Object.entries(userData).forEach(([date, event]) => {
                const typedEvent = event as AbsenceAdditional
                const events: VacationItem[] = []

                const vacationKeys = Object.keys(vacationTypes) as VacationFlag[]

                vacationKeys.forEach((flag) => {
                    if (typedEvent[flag]) {
                        events.push({
                            type: flag,
                            id: flag === 'isAbsence'
                              ? typedEvent.absenceInfo?.id ?? null
                              : undefined,
                            isInternal: flag === 'isAbsence'
                              ? typedEvent.absenceInfo?.isInternal
                              : undefined
                        })
                    }
                })

                if (events.length && user.scheduleTimeShift) {
                    user.scheduleTimeShift[date] = events
                    user.hasVacation = true
                }
            })
        })

        state.pharm_schedule = payload
    },
    setFondWorkTimeHuman(state, payload) {
        state.pharm_users.map((user: NewUser) => {
            user.frv = emptyFRVObj(user.externalUser, user.zupGuid, state.pharmId)
            payload.forEach((item: HumanFRV) => {
                if (user.zupGuid === item.zupGuid) {
                    // Добавляем тип external для внешних пользователей (OMR-3846)
                    user.frv = item
                    user.frv.frvType = user.externalUser ? 'external' : item.frvType
                    user.frv.frvNew = 0
                    user.frv.frvRvNew = 0
                    user.numberVs = item.numberVs
                }
            })
            return user
        })
    },
    resetFondWorkTimeHuman(state) {
        state.pharm_users.map((user: NewUser) => {
            user.frv = emptyFRVObj(user.externalUser, user.zupGuid, state.pharmId)
            user.frv.frvNew = 0
            user.frv.frvRvNew = 0
            return user
        })
    },
    saveHumanFRV(state, payload) {
        const user = state.pharm_users.find(user => user.zupGuid === payload.zupGuid)
        if (user?.frv) {
            user ? user.old_frv = {
                frvSchedule: user.frv.frvSchedule,
                frvNew: user.frv.frvNew,
                frvRvNew: user.frv.frvRvNew,
                frvRvSchedule: user.frv.frvRvSchedule
            } : null
        }
    },
    changeHumanFRV(state, payload) {
        const user = state.pharm_users.find(user => user.zupGuid === payload.user.zupGuid)

        // Изменяем ФРВ сотрудника (OMR-3075)
        if (user?.frv && user?.old_frv) {
            user.frv.frvSchedule = (user.frv.frvSchedule + payload.frv * -1)
            user.frv.frvRvSchedule = (user.frv.frvRvSchedule + payload.frvRV * -1)
        }
    },
    resetHumanFRV(state, payload) {
        const user = state.pharm_users.find(user => user.zupGuid === payload.user.zupGuid)
        if (user?.frv && user?.old_frv) {
            user.frv.frvRvSchedule = user.old_frv.frvRvSchedule
            user.frv.frvSchedule = user.old_frv.frvSchedule
            user.frv.frvNew = user.old_frv.frvNew
            user.frv.frvRvNew = user.old_frv.frvRvNew
        }
    },
    setHolidays(state, payload) {
        state.pharm_holidays = payload
    },
    // Праздники для модалки создания потребности Беру — не трогают список главного экрана
    setBeruHolidays(state, payload) {
        state.beru_holidays = payload
    },
    setShiftsExistence(state, payload) {
        state.shift_existence = payload
    },
    changeTimeShift(state, payload) {
        state.pharm_users.map((item) => {
            if (item.zupGuid !== payload.zupGuid) return;
            const isValidFrvType = item.frv?.frvType && frvTypeList.includes(item.frv?.frvType);
            const isPharmFrvCounted = isValidFrvType && !item.profiPstUser;

            if (item) {
                item.frv.frvNew = payload.timeShift
            }

            if (!payload.holiday && isPharmFrvCounted && state.corestaff_count_panel) {
                state.corestaff_count_panel.new = payload.timeShift
            }
            if ((payload.frvRv >= 0 && payload.holiday) && isPharmFrvCounted && state.corestaff_count_panel) {
                state.corestaff_count_panel.new = payload.timeShift
            }
        })
    },
    changeFRV_RVTimeShift(state, payload) {
        state.pharm_users.map((item) => {
            if (item.zupGuid !== payload.zupGuid) return;
            const isValidFrvType = item.frv?.frvType && frvTypeList.includes(item.frv?.frvType);
            // ФРВ РВ аптеки по сотрудникам ПрофиПСТ не считаем
            const isPharmFrvCounted = isValidFrvType && !item.profiPstUser;

            if (item.frv) {
                item.frv.frvRvNew = payload.frvRv
            }

            if (payload.frvRv >= 0 && isPharmFrvCounted && state.frv_rv_count_panel) {
                state.frv_rv_count_panel.new = payload.frvRv
            }
        })
    },
    // Обновление новых часов в панелях счётчиков модалки Беру без привязки к сотруднику
    changeBeruFRV(state, payload) {
        if (state.beru_corestaff_count_panel) {
            state.beru_corestaff_count_panel.new = payload.frv
        }
        if (state.beru_frv_rv_count_panel) {
            state.beru_frv_rv_count_panel.new = payload.frvRV
        }
    },
    closeErrorDialog(state, payload) {
        state.error_modal = payload
        
    },
    addUser(state, payload) {
        const pharmTz = state.pharmacies_arr[0].pharmTz

        const findUserDuplicate = state.pharm_users.some(user => user.zupGuid === payload.zupGuid)
        if (!findUserDuplicate) {
            state.pharm_users.push({
                name: `${payload.lastName} ${payload.firstName} ${payload.middleName}`,
                position: payload.position,
                positionCode: '',
                bid: payload.bid,
                zupGuid: payload.zupGuid,
                frv: emptyFRVObj(true, payload.zupGuid, state.pharmId),
                id: payload.id,
                timeShift: {},
                scheduleTimeShift: {},
                dateArr: null,
                timeZone: pharmTz,
                grade: 0,
                hasVacation: false,
                numberVs: 0,
                externalUser: true,
                profiPstUser: false,
                newUser: true,
                phone: null,
                dateDismissal: payload.dateDismissal
            })
        } else {
            store.commit('data/closeErrorDialog', 'Данный сотрудник уже добавлен. Выберите другого сотрудника')
        }
    },
    deleteUser(state, payload) {
        state.pharm_users = state.pharm_users.filter(user => user.zupGuid !== payload)
    },
    setUserSearchList(state, payload) {
        state.search_user_list = payload
    },
    setUserSearchSkip(state, payload) {
        state.search_user_skip = payload
    },
    setUserSearchCount(state, payload) {
        state.search_user_count = payload
    },
    increaseUserSearchQuery(state) {
        if (state.search_user_skip < state.search_user_count) {
            state.search_user_skip += 5
        }
    },
    decreaseUserSearchQuery(state) {
        if (state.search_user_skip > 0) {
            state.search_user_skip -= 5
        }
    },
    clearOpenedCreateShift(state) {
        state.active_shift_details = null
    },
    setShiftInfo(state, payload) {
        state.shift_info = null

        // Собираем все смены по типу в shiftStatusItems
        const shiftStatusItems: ShiftInfoItem[] = [];

        if (payload.createdBy) {
            shiftStatusItems.push({
              ...payload.createdBy,
              type: "created",
              label: "Создание",
            });
        } else {
            // Если не пришёл createdBy - создаём элемент с уведомлением
            shiftStatusItems.unshift({
              lastName: "",
              firstName: "",
              position: "Смена создана автоматически",
              dateFrom: "",
              dateTo: "",
              type: "created",
              label: "Создание"
            });
        }

        if (Array.isArray(payload.updatedBy)) {
            for (const item of payload.updatedBy) {
                shiftStatusItems.push({
                    ...item,
                    type: 'updated',
                    label: 'Редактирование'
                });
            }
        }

        if (payload.canceledBy) {
            shiftStatusItems.push({
                ...payload.canceledBy,
                type: 'cancelled',
                label: 'Отмена'
            });
        }

        state.shift_info = {
            ...payload,
            shiftStatusItems
        }
    },
    clearShiftInfo(state) {
        state.shift_info = null
    },
    setPharmTz(state, payload) {
        state.pharmTz = payload
    },
    setAdminShiftBuffer(state, payload) {
        state.admin_shift_buffer = payload
    }
}

export default mutations